import { StatusCodes } from "http-status-codes"
// @ts-expect-error TS(7016) FIXME: Could not find a declaration file for module 'uuid... Remove this comment to see the full error message
import { v4 as uuid } from "uuid"
import ResponseError from "../../types/responseError"
import { reportError } from "../../utils/ErrorReporting"
import Settings from "../../utils/CarrotConfig"
import { HasCustomNetworksResponse } from "#/pages/provider-finder/types/HasCustomNetworksResponse"
import { ExtendedProvidersTypeaheadProvider } from "#/pages/provider-finder/types/Providers"

export type ProviderCount = {
  hasFertilityProviders: boolean
  hasUrologyProviders: boolean
  hasAdoptionProviders: boolean
  hasCryobanksProviders: boolean
  hasLowTProviders: boolean
  hasMenopauseProviders: boolean
  hasGenderAffirmingHormoneTherapyProviders: boolean
  hasDonorAssistedReproductionProviders: boolean
  hasGestationalCarrierProviders: boolean
  // TODO in OP-2783 we need to add hasDoulasProviders to the get-has-providers-by-type response so that this value is populated
  hasDoulasProviders: boolean
  hasProviders: boolean
}

export type ProviderNetwork = { providerNetworkIdentifier: string }

class ProviderFinderClientError extends Error {
  readonly correlationId?: string

  constructor(message: string, { cause, correlationId }: { cause?: Error; correlationId?: string }) {
    super(message)
    this.name = this.constructor.name
    this.cause = cause
    this.correlationId = correlationId

    Object.setPrototypeOf(this, ProviderFinderClientError.prototype)
  }
}

export class ProviderFinderClient {
  baseUrl: string
  handleSessionExpiration: () => void

  constructor(baseUrl: string, handleSessionExpiration: () => void) {
    this.baseUrl = baseUrl
    this.handleSessionExpiration = handleSessionExpiration
  }

  async processJsonResponse<ResponseType>(response: Response, correlationId: string): Promise<ResponseType> {
    if (response.status === StatusCodes.OK) {
      return response?.json()
    }
    throw new ResponseError(response, correlationId)
  }

  createJsonHeaders(correlationId: string): Headers {
    const headers = new Headers()
    headers.append("X-Request-ID", correlationId)
    headers.append("X-React-App-Version", __APP_VERSION__ || "local")
    headers.append("Content-Type", "application/json")
    headers.append("X-Authorizer-Domain", Settings.AUTHORIZER_DOMAIN)
    return headers
  }

  async get<ResponseType>(url: RequestInfo | URL): Promise<ResponseType> {
    const correlationId = uuid()

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: this.createJsonHeaders(correlationId),
        credentials: "include"
      })

      return await this.processJsonResponse(response, correlationId)
    } catch (error) {
      error.correlationId = correlationId

      if (error instanceof ResponseError && error.statusCode === StatusCodes.UNAUTHORIZED) {
        this.handleSessionExpiration()
        return null
      }

      reportError(
        new ProviderFinderClientError(`Failed to fetch ${url} `, {
          cause: error, // TODO Sentry groups this based on a custom grouping rule (src/utils/ErrorReporting.ts) with a wide net of anything with "Failed to fetch" in the stack trace.
          correlationId
        })
      )

      throw error
    }
  }

  async getHasProviderTypeInCountry(companyCountryCode: string): Promise<ProviderCount> {
    const url = new URL(`${this.baseUrl}/has-provider-type-in-country`)
    url.searchParams.set("companyCountryCode", companyCountryCode)

    return this.get(url)
  }

  async getHasCustomNetworks(): Promise<HasCustomNetworksResponse> {
    const url = new URL(`${this.baseUrl}/has-custom-network`)
    return this.get(url)
  }

  async getExtendedProvidersTypeahead(
    providerNameSubstring: string,
    providerType?: string,
    countryCode?: string | null
  ): Promise<ExtendedProvidersTypeaheadProvider[]> {
    const url = new URL(`${this.baseUrl}/search-all-providers`)
    url.searchParams.set("providerNameSubstring", providerNameSubstring)
    if (providerType) {
      url.searchParams.set("providerType", providerType)
    }
    if (countryCode != null) {
      url.searchParams.set("countryCode", countryCode)
    }

    const response = await this.get<
      Array<{ providerUuid: number; providerName: string; providerEligibilityStatus: string }>
    >(url)

    // Map response to ExtendedProvidersTypeaheadProvider format
    return response.map((provider) => ({
      uuid: provider.providerUuid,
      name: provider.providerName,
      providerEligibilityStatus: provider.providerEligibilityStatus
    }))
  }
}
